import React, { useMemo, useCallback } from "react";
import type { LucideIcon } from "lucide-react-native";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import {
  FlaskConical,
  Scan,
  Leaf,
  Atom,
  AlertTriangle,
  Thermometer,
  Plus,
} from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useExposure, ExposureType } from "../ExposureContext";
import { EXPOSURE_OPTIONS } from "../options";

const EXPOSURE_ICONS: Record<ExposureType, LucideIcon> = {
  chemicals: FlaskConical,
  x_rays: Scan,
  agricultural_area: Leaf,
  nuclear: Atom,
  toxins: AlertTriangle,
  prolonged_heat: Thermometer,
  other_exposure: Plus,
};

export function ExposureTypesScreen() {
  const t = useTranslations();
  const { state, updateState, next } = useExposure({ name: "ExposureTypes" });
  const navigation = useNavigation();

  const selected = useMemo(
    () => new Set(state.selectedExposures || []),
    [state.selectedExposures],
  );

  const toggleSelection = useCallback(
    (type: ExposureType) => {
      const newSelection = new Set(selected);
      if (newSelection.has(type)) {
        newSelection.delete(type);
      } else {
        newSelection.add(type);
      }

      const selectionArray = Array.from(newSelection);
      const currentDetailsByExposure = new Map(
        state.exposureDetails.map((detail) => [detail.exposure, detail]),
      );

      const nextDetails = selectionArray.map((exposureType) => {
        const existing = currentDetailsByExposure.get(exposureType);
        if (existing) {
          return existing;
        }
        return {
          id: undefined,
          exposure: exposureType,
          frequency: null,
          context: null,
          other: null,
        };
      });

      const idsToDelete = Array.from(
        new Set(
          state.exposureDetails
            .filter(
              (detail) =>
                detail.exposure && !selectionArray.includes(detail.exposure),
            )
            .map((detail) => detail.id)
            .filter((id): id is string => Boolean(id)),
        ),
      );

      updateState({
        selectedExposures: selectionArray,
        exposureDetails: nextDetails,
        exposureIdsToDelete: idsToDelete,
      });
    },
    [selected, state.exposureDetails, updateState],
  );

  const handleSave = useCallback(() => {
    if (selected.size === 0) {
      return;
    }
    next();
  }, [selected.size, next]);

  return (
    <QuestionLayout
      title={t("med_history.lifestyle.exposure.types_title")}
      subtitle={t("med_history.lifestyle.exposure.types_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selected.size === 0}
      saveLabel={t("med_history.lifestyle.continue")}
    >
      <View style={styles.optionsGrid}>
        {EXPOSURE_OPTIONS.map((option) => {
          const isSelected = selected.has(option.id);
          const Icon = EXPOSURE_ICONS[option.id as ExposureType];
          if (!Icon) {
            return null;
          }
          return (
            <View key={option.id} style={styles.optionWrapper}>
              <SquareOptionButton
                icon={Icon}
                label={t(option.labelKey)}
                isSelected={isSelected}
                onPress={() => toggleSelection(option.id)}
                iconSize={20}
                containerSize={40}
                labelSize="sm"
              />
            </View>
          );
        })}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
});
